import generateHeading from 'components/pages/area-listings/util/generate-heading';
import generateLocalizedHeading from 'components/pages/area-listings/util/generate-localized-heading';
import { translatePropertyType } from 'components/pages/area-listings/util/translate-property-type';
import { LOCALE_COMMON_PATHS } from 'types/locales';

import type { AddressType } from '@zoocasa/go-search';
import type { Filter } from 'contexts/preferences/listing-params/types';

type HeadingTranslator = (key: string, options?: Record<string, unknown>) => string;

type FilterHeadingParams = {
  areaName: string;
  areaType: AddressType;
  filter: Pick<Filter, 'rental' | 'status' | 'homeType'>;
  city?: string;
  neighbourhood?: string;
};

// generateHeading runs the whole string through capitalizeWords, which lower-cases
// two-letter words like "on" (Ontario). Restore a trailing province or state code.
function withUppercaseProvinceCode(heading: string): string {
  return heading.replace(/,\s*([A-Za-z]{2})\b/g, (_match, code: string) => `, ${code.toUpperCase()}`);
}

// The sold/pending/past keys lead with `{{areaName}}`, so a location-less heading (`/search`,
// where the viewport rather than a named area decides the results) interpolates to a leading
// gap: " Recently Sold Real Estate & Homes".
function withCollapsedSpacing(heading: string): string {
  return heading.replace(/\s+/g, ' ').trim();
}

/**
 * Filter-aware heading ("Real Estate & Homes for Sale in Toronto", "Toronto Recently Sold Condos").
 * The sentence and the property type come from the locale files. The English
 * generateHeading result is only the fallback when a key is missing.
 */
export function localizedFilterHeading(params: FilterHeadingParams, t: HeadingTranslator): string {
  const english = generateHeading(params);
  const localized = generateLocalizedHeading(params);
  // The locale strings already have their casing. capitalizeWords would rewrite
  // French ("récemment" becomes "Éécemment"), so only the province code is restored.
  return withCollapsedSpacing(withUppercaseProvinceCode(t(localized.key, {
    ...localized.values,
    propertyType: translatePropertyType(localized.values.propertyType, t),
    defaultValue: english,
  })));
}

/**
 * Unfiltered desktop heading ("Real Estate Property Listings in Toronto, ON").
 * This sentence has no shared area-page key, so it lives on area_page_v2.
 * `fallback` is the English string already built for the page.
 */
export function localizedListingsHeading(areaName: string | undefined, fallback: string, t: HeadingTranslator): string {
  const areaLabel = areaName?.trim() ?? '';
  if (!areaLabel) {
    return t(LOCALE_COMMON_PATHS.AREA_PAGE_V2_LISTINGS_HEADING_GENERIC, { defaultValue: fallback });
  }
  return withUppercaseProvinceCode(t(LOCALE_COMMON_PATHS.AREA_PAGE_V2_LISTINGS_HEADING_IN_AREA, {
    areaName: areaLabel,
    defaultValue: fallback,
  }));
}
